import React, { useState, useRef, useEffect } from 'react';
import { Check, Image, Palette, Upload } from 'lucide-react';
import { searchImages } from '../../services/unsplash-service';
import { uploadSiteImage, compressImage, fileToBase64 } from '../../services/image-upload-service';
import { deleteFile } from '../../services/storage-service';
import ToggleGroup from '../common/ToggleGroup';
import ColorPicker from './ColorPicker';
import { getTextColor } from '../../utils/colorUtils';

const HERO_TYPES = [
  { id: 'color', name: 'Solid Color', icon: Palette },
  { id: 'gradient', name: 'Gradient', icon: Palette },
  { id: 'image', name: 'Stock Image', icon: Image },
  { id: 'custom-image', name: 'Your Image', icon: Upload },
];

const GRADIENT_DIRECTIONS = [
  { id: 'to bottom', name: 'Top to Bottom' },
  { id: 'to right', name: 'Left to Right' },
  { id: 'to bottom right', name: 'Diagonal' },
  { id: '135deg', name: 'Reverse Diagonal' },
];

const OVERLAY_OPTIONS = [
  { id: 'none', name: 'None', class: '', value: 0 },
  { id: 'light', name: 'Light', class: 'bg-black/20', value: 0.2 },
  { id: 'medium', name: 'Medium', class: 'bg-black/40', value: 0.4 },
  { id: 'dark', name: 'Dark', class: 'bg-black/60', value: 0.6 },
];

const TEXT_ALIGNMENTS = ['Left', 'Center', 'Right'];
const HERO_HEIGHTS = ['Short', 'Medium', 'Tall'];
const IMAGE_POSITIONS = ['Top', 'Center', 'Bottom'];

const HeroSettings = ({ heroSettings = {}, onUpdate, colors = {} }) => {
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Drag-to-reposition state
  const [isRepositioning, setIsRepositioning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 }); // Percentage offset from center (-40 to 40)
  const [isUploading, setIsUploading] = useState(false);
  const previewRef = useRef(null);

  // Track drag info with useRef for reliable access in event handlers
  const dragInfo = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initialPosX: 0,
    initialPosY: 0
  });

  const settings = {
    type: heroSettings.type || 'color',
    heroColor: heroSettings.heroColor || null,
    gradientStartColor: heroSettings.gradientStartColor || null,
    gradientEndColor: heroSettings.gradientEndColor || null,
    gradientDirection: heroSettings.gradientDirection || 'to bottom',
    imageId: heroSettings.imageId || null,
    imageUrl: heroSettings.imageUrl || null,
    imageAttribution: heroSettings.imageAttribution || null,
    imageAttributionUrl: heroSettings.imageAttributionUrl || null,
    customImageUrl: heroSettings.customImageUrl || null,
    customImageStoragePath: heroSettings.customImageStoragePath || null,
    overlay: heroSettings.overlay || 'none',
    textAlign: heroSettings.textAlign || 'center',
    height: heroSettings.height || 'medium',
    imageVerticalPosition: heroSettings.imageVerticalPosition || 'center',
    imageRepositionOffset: heroSettings.imageRepositionOffset || { x: 0, y: 0 },
  };

  // Initialize image position from existing settings
  useEffect(() => {
    if (heroSettings.imageRepositionOffset) {
      setImagePosition(heroSettings.imageRepositionOffset);
    }
  }, [heroSettings.imageRepositionOffset]);

  // Initialize selectedImage from existing settings
  React.useEffect(() => {
    if (heroSettings.imageId && heroSettings.imageUrl) {
      setSelectedImage({
        id: heroSettings.imageId,
        urls: { regular: heroSettings.imageUrl },
        user: { name: heroSettings.imageAttribution || 'Unsplash' },
      });
    }
  }, [heroSettings.imageId, heroSettings.imageUrl, heroSettings.imageAttribution]);

  const updateSetting = (key, value) => {
    onUpdate({ ...settings, [key]: value });
  };

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      const results = await searchImages(query);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectImage = (image) => {
    setSelectedImage(image);
    setImagePosition({ x: 0, y: 0 }); // Reset reposition when selecting new image
    onUpdate({
      ...settings,
      type: 'image',
      imageUrl: image.urls.regular,
      imageId: image.id,
      imageAttribution: image.user.name,
      imageAttributionUrl: image.user.links?.html,
      imageVerticalPosition: settings.imageVerticalPosition || 'center',
      imageRepositionOffset: { x: 0, y: 0 },
    });
  };

  const handleCustomImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';

    // Capture current settings before async operations (state may update mid-flight)
    const currentSettings = { ...settings };
    setImagePosition({ x: 0, y: 0 });
    setIsUploading(true);

    // Step 1: Optimistic update — show Base64 preview immediately
    let base64Preview;
    try {
      base64Preview = await fileToBase64(file);
    } catch (err) {
      setIsUploading(false);
      return;
    }
    onUpdate({
      ...currentSettings,
      type: 'custom-image',
      customImageUrl: base64Preview,
      customImageStoragePath: null, // Clear old path — we'll delete old file next
      imageVerticalPosition: currentSettings.imageVerticalPosition || 'center',
      imageRepositionOffset: { x: 0, y: 0 },
    });

    // Step 2: Delete previous Storage image if one exists
    if (currentSettings.customImageStoragePath) {
      try {
        await deleteFile(currentSettings.customImageStoragePath);
      } catch (err) {
        console.warn('⚠️ Could not delete old hero image from Storage:', err.message);
      }
    }

    // Step 3: Compress to max 1920px / 0.8 quality and upload to Storage
    try {
      const compressedBlob = await compressImage(file, 1920, 0.8);
      const safeName = file.name.replace(/\.[^/.]+$/, '') + '.jpg';
      const compressedFile = new File([compressedBlob], safeName, { type: 'image/jpeg' });
      const result = await uploadSiteImage(compressedFile, 'hero');
      if (result.success && result.source === 'storage') {
        // Replace Base64 with permanent Storage URL
        onUpdate({
          ...currentSettings,
          type: 'custom-image',
          customImageUrl: result.url,
          customImageStoragePath: result.fullPath,
          imageVerticalPosition: currentSettings.imageVerticalPosition || 'center',
          imageRepositionOffset: { x: 0, y: 0 },
        });
      }
      // source === 'base64': Storage unavailable — Base64 preview stays in state
    } catch (err) {
      console.warn('⚠️ Hero image Storage upload failed, keeping Base64 preview:', err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleTypeChange = async (newType) => {
    // Clean up Storage image when switching away from custom-image
    if (settings.type === 'custom-image' && newType !== 'custom-image' && settings.customImageStoragePath) {
      try {
        await deleteFile(settings.customImageStoragePath);
      } catch (err) {
        console.warn('⚠️ Could not delete hero image from Storage:', err.message);
      }
    }
    updateSetting('type', newType);
  };

  // Mouse drag start (vertical-only repositioning)
  const handleDragStart = (e) => {
    if (!isRepositioning) return;
    e.preventDefault();

    // Store starting Y position only (vertical-only)
    dragInfo.current = {
      startY: e.clientY,
      startPosY: imagePosition.y
    };

    setIsDragging(true);

    // Add global listeners
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Mouse drag move - vertical only (ignores horizontal movement)
  const handleMouseMove = (e) => {
    e.preventDefault();

    // Only calculate vertical movement
    const deltaY = e.clientY - dragInfo.current.startY;

    // Convert to percentage: 10 pixels = 1%
    const percentY = deltaY / 10;

    // Add to starting position
    const newY = dragInfo.current.startPosY + percentY;

    // Constrain to bounds (-40 to +40)
    const boundedY = Math.max(-40, Math.min(40, newY));

    // X stays at 0 (centered), only Y changes
    setImagePosition({ x: 0, y: boundedY });
  };

  // Mouse drag end
  const handleMouseUp = () => {
    setIsDragging(false);
    dragInfo.current = { startY: 0, startPosY: 0 };

    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  // Touch drag start (vertical-only)
  const handleTouchStart = (e) => {
    if (!isRepositioning) return;

    const touch = e.touches[0];

    dragInfo.current = {
      startY: touch.clientY,
      startPosY: imagePosition.y
    };

    setIsDragging(true);

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
  };

  // Touch drag move - vertical only
  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];

    const deltaY = touch.clientY - dragInfo.current.startY;
    const percentY = deltaY / 10;
    const newY = dragInfo.current.startPosY + percentY;
    const boundedY = Math.max(-40, Math.min(40, newY));

    // X stays at 0, only Y changes
    setImagePosition({ x: 0, y: boundedY });
  };

  // Touch drag end
  const handleTouchEnd = () => {
    setIsDragging(false);
    dragInfo.current = { startY: 0, startPosY: 0 };

    window.removeEventListener('touchmove', handleTouchMove);
    window.removeEventListener('touchend', handleTouchEnd);
  };

  // Save position when repositioning ends
  useEffect(() => {
    if (!isRepositioning && (imagePosition.x !== 0 || imagePosition.y !== 0)) {
      // Position changed and we exited reposition mode - save it
      onUpdate({ ...heroSettings, imageRepositionOffset: imagePosition });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRepositioning]);

  const handleResetPosition = () => {
    setImagePosition({ x: 0, y: 0 });
    onUpdate({ ...settings, imageRepositionOffset: { x: 0, y: 0 } });
  };

  const handleDoneRepositioning = () => {
    setIsRepositioning(false);
    onUpdate({ ...settings, imageRepositionOffset: imagePosition });
  };

  // Get current hero background style for preview (non-image types only)
  const getHeroPreviewStyle = () => {
    switch (settings.type) {
      case 'color':
        return { backgroundColor: settings.heroColor || colors.primary || '#5D1CE1' };
      case 'gradient':
        return {
          background: `linear-gradient(${settings.gradientDirection || 'to bottom'}, ${settings.gradientStartColor || colors.primary || '#5D1CE1'}, ${settings.gradientEndColor || colors.secondary || '#BB9749'})`,
        };
      case 'image':
      case 'custom-image':
        // Images now use img tag, not background
        return { backgroundColor: 'transparent' };
      default:
        return { backgroundColor: settings.heroColor || colors.primary || '#5D1CE1' };
    }
  };

  // Get the current image URL
  const getCurrentImageUrl = () => {
    if (settings.type === 'image' && settings.imageUrl) return settings.imageUrl;
    if (settings.type === 'custom-image' && settings.customImageUrl) return settings.customImageUrl;
    return null;
  };

  const getOverlayClass = () => {
    return OVERLAY_OPTIONS.find(o => o.id === settings.overlay)?.class || '';
  };

  // Check if hero has content to show preview
  const hasHeroContent =
    settings.type === 'color' ||
    settings.type === 'gradient' ||
    (settings.type === 'image' && settings.imageUrl) ||
    (settings.type === 'custom-image' && settings.customImageUrl);

  // Check if we should show image controls
  const showImageControls = (settings.type === 'image' && settings.imageUrl) ||
                            (settings.type === 'custom-image' && settings.customImageUrl);

  // Compute mock element color for the mini preview skeleton (WCAG auto-contrast)
  const previewMockColor = (() => {
    if (settings.type === 'color') {
      return getTextColor(settings.heroColor || colors.primary || '#5D1CE1');
    }
    if (settings.type === 'gradient') {
      return getTextColor(settings.gradientStartColor || colors.primary || '#5D1CE1');
    }
    return '#FFFFFF'; // image types always use white skeleton
  })();

  // Preview heights based on selection
  const previewHeights = { short: 'h-32', medium: 'h-40', tall: 'h-52' };
  const previewHeightClass = previewHeights[settings.height] || 'h-40';

  return (
    <div className="space-y-5">
      {/* Hero Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Background Style</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {HERO_TYPES.map((type) => {
            const isSelected = settings.type === type.id;
            const Icon = type.icon;
            return (
              <span
                key={type.id}
                role="button"
                tabIndex={0}
                onClick={() => handleTypeChange(type.id)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTypeChange(type.id); }}
                className={`relative flex flex-col items-center p-2.5 rounded-lg border-2 transition-all cursor-pointer select-none ${
                  isSelected
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                {isSelected && (
                  <div className="absolute top-1 right-1 w-4 h-4 bg-purple-600 rounded-full flex items-center justify-center">
                    <Check className="w-2.5 h-2.5 text-white" />
                  </div>
                )}
                <Icon className={`w-4 h-4 mb-1 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                <span className={`block w-full text-xs font-medium text-center leading-tight ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                  {type.name}
                </span>
              </span>
            );
          })}
        </div>
      </div>

      {/* Solid Color Picker */}
      {settings.type === 'color' && (
        <div>
          <ColorPicker
            value={settings.heroColor || colors.primary || '#5D1CE1'}
            onChange={(color) => updateSetting('heroColor', color)}
            label="Background Color"
          />
        </div>
      )}

      {/* Gradient Options */}
      {settings.type === 'gradient' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Gradient Direction</label>
            <ToggleGroup
              columns={2}
              options={GRADIENT_DIRECTIONS.map(dir => ({ value: dir.id, label: dir.name }))}
              value={settings.gradientDirection || 'to bottom'}
              onChange={(val) => updateSetting('gradientDirection', val)}
            />
          </div>
          <ColorPicker
            value={settings.gradientStartColor || colors.primary || '#5D1CE1'}
            onChange={(color) => updateSetting('gradientStartColor', color)}
            label="Start Color"
          />
          <ColorPicker
            value={settings.gradientEndColor || colors.secondary || '#BB9749'}
            onChange={(color) => updateSetting('gradientEndColor', color)}
            label="End Color"
          />
        </div>
      )}

      {/* Stock Image Search */}
      {settings.type === 'image' && (
        <div className="space-y-3">
          {/* Search Input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search images..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <button
              onClick={() => handleSearch()}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-white rounded outline-none"
              style={{ padding: '4px 8px', border: '2px solid transparent', boxSizing: 'border-box', minWidth: '52px', backgroundColor: '#7c3aed', transition: 'background-color 0.15s, color 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#6d28d9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#7c3aed'; }}
            >
              Search
            </button>
          </div>

          {/* Quick Suggestion Tags */}
          <div className="flex flex-wrap gap-1.5">
            {['spiritual', 'nature', 'meditation', 'crystals', 'wellness', 'abstract', 'stars', 'forest'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setSearchQuery(suggestion);
                  handleSearch(suggestion);
                }}
                className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-purple-100 hover:text-purple-600 rounded-full transition-colors capitalize"
              >
                {suggestion}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {isSearching && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
              <span className="ml-2 text-sm text-gray-600">Searching...</span>
            </div>
          )}

          {/* Results Grid */}
          {searchResults.length > 0 && !isSearching && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {searchResults.map((image) => (
                <button
                  key={image.id}
                  onClick={() => handleSelectImage(image)}
                  className={`relative aspect-video rounded-lg overflow-hidden transition-all hover:opacity-90 ${
                    selectedImage?.id === image.id ? 'ring-2 ring-purple-600 ring-offset-2' : ''
                  }`}
                >
                  <img
                    src={image.urls?.small}
                    alt={image.alt_description || 'Unsplash image'}
                    className="w-full h-full object-cover"
                  />
                  {/* Selected indicator - checkmark with colored circle background */}
                  {selectedImage?.id === image.id && (
                    <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-md">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Initial state */}
          {!hasSearched && !isSearching && !settings.imageUrl && (
            <div className="text-center py-6 bg-white rounded-lg border border-dashed border-gray-200">
              <span className="text-3xl mb-2 block">🖼️</span>
              <p className="text-sm text-gray-500">Search or click a suggestion</p>
            </div>
          )}

          {/* No Results */}
          {searchResults.length === 0 && hasSearched && !isSearching && (
            <div className="text-center py-6 bg-white rounded-lg">
              <p className="text-sm text-gray-600">No images found. Try different keywords.</p>
            </div>
          )}

          {/* Unsplash Attribution */}
          <p className="text-xs text-gray-400 text-center">
            Photos by <a href="https://unsplash.com?utm_source=divine_frameworks&utm_medium=referral" target="_blank" rel="noopener noreferrer" className="underline">Unsplash</a>
          </p>
        </div>
      )}

      {/* Custom Image Upload */}
      {settings.type === 'custom-image' && (
        <div>
          {settings.customImageUrl ? (
            <div>
              {isUploading ? (
                <div className="flex items-center gap-1.5">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600" />
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Uploading...</span>
                </div>
              ) : (
                <label
                  role="button"
                  tabIndex={0}
                  style={{ color: '#7c3aed', fontSize: 13, cursor: 'pointer', userSelect: 'none', display: 'inline-block' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                >
                  Change image
                  <input type="file" accept="image/*" onChange={handleCustomImageUpload} className="hidden" />
                </label>
              )}
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full aspect-[2/1] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 hover:bg-purple-50/30 transition-colors">
              <Upload className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-sm text-gray-600">Click to upload</span>
              <span className="text-xs text-gray-400">1920x1080 recommended</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleCustomImageUpload}
                className="hidden"
              />
            </label>
          )}
        </div>
      )}

      {/* PREVIEW - Show when hero has content */}
      {hasHeroContent && (
        <div className="space-y-3">
          <div
            ref={previewRef}
            className={`relative rounded-lg overflow-hidden ${
              isRepositioning
                ? 'ring-2 ring-purple-500 ring-offset-2 cursor-move'
                : 'border border-gray-200'
            }`}
            style={{ userSelect: 'none', touchAction: 'none' }}
            onMouseDown={showImageControls ? handleDragStart : undefined}
            onTouchStart={showImageControls ? handleTouchStart : undefined}
          >
            {/* Instructions when in reposition mode */}
            {isRepositioning && showImageControls && (
              <div className="absolute inset-0 bg-black bg-opacity-20 z-20 pointer-events-none flex items-center justify-center">
                <div className="bg-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium text-gray-700">
                  Drag up or down to adjust
                </div>
              </div>
            )}

            {/* Background layer - vertical-only repositioning */}
            <div
              className={`relative ${previewHeightClass}`}
              style={{
                ...getHeroPreviewStyle(),
                // For images, use background-image with vertical-only repositioning
                ...(getCurrentImageUrl() ? {
                  backgroundImage: `url(${getCurrentImageUrl()})`,
                  backgroundSize: 'cover',
                  backgroundPosition: `50% ${50 + imagePosition.y}%`,
                  backgroundRepeat: 'no-repeat',
                  transition: isDragging ? 'none' : 'background-position 0.2s ease',
                  cursor: isRepositioning ? 'ns-resize' : 'default'
                } : {})
              }}
            >
              {/* Overlay */}
              {(settings.type === 'image' || settings.type === 'custom-image') && (
                <div className={`absolute inset-0 ${getOverlayClass()} pointer-events-none`} />
              )}

              {/* Content Preview */}
              <div
                className={`relative z-10 h-full flex flex-col justify-center p-4 ${
                  settings.textAlign === 'left' ? 'items-start text-left' :
                  settings.textAlign === 'right' ? 'items-end text-right' :
                  'items-center text-center'
                }`}
              >
                <div className="w-10 h-10 rounded-full mb-2" style={{ backgroundColor: previewMockColor, opacity: 0.3 }} />
                <div className="h-4 w-28 rounded mb-1.5" style={{ backgroundColor: previewMockColor, opacity: 0.8 }} />
                <div className="h-2.5 w-20 rounded mb-3" style={{ backgroundColor: previewMockColor, opacity: 0.5 }} />
                <div className="h-6 w-16 rounded-md" style={{ backgroundColor: previewMockColor }} />
              </div>

              {/* Attribution badge */}
              {settings.type === 'image' && settings.imageAttribution && (
                <div className="absolute bottom-1 right-2 text-[10px] text-white/50 z-10">
                  Unsplash
                </div>
              )}
            </div>
          </div>

          {/* Reposition Controls - Only for images (vertical-only) */}
          {showImageControls && (
            <div className="flex items-center gap-3">
              {!isRepositioning ? (
                <button
                  type="button"
                  onClick={() => setIsRepositioning(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-white border border-purple-200 rounded-lg hover:bg-purple-50 transition-colors group relative"
                >
                  <span className="text-base leading-none">⇕</span>
                  <span>Reposition</span>
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    Drag up/down to adjust vertical position
                  </div>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={handleDoneRepositioning}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPosition}
                    className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Reset to Center
                  </button>
                </>
              )}
              {imagePosition.y !== 0 && !isRepositioning && (
                <span className="text-xs text-gray-400">
                  Adjusted
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Segmented Controls - Show when hero has content */}
      {hasHeroContent && (
        <div className="space-y-4">
          {/* Row 1: Position (if image), Alignment, Height */}
          <div className="space-y-4">
            {/* Text Alignment */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Text Alignment
              </label>
              <ToggleGroup
                options={TEXT_ALIGNMENTS.map(align => ({ value: align.toLowerCase(), label: align }))}
                value={settings.textAlign || 'center'}
                onChange={(val) => updateSetting('textAlign', val)}
              />
            </div>

            {/* Height */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Height
              </label>
              <ToggleGroup
                options={HERO_HEIGHTS.map(h => ({ value: h.toLowerCase(), label: h }))}
                value={settings.height || 'medium'}
                onChange={(val) => updateSetting('height', val)}
              />
            </div>
          </div>

          {/* Row 2: Image Overlay - Only for image types */}
          {showImageControls && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Image Overlay
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Add a dark overlay to improve text readability
              </p>
              <ToggleGroup
                options={OVERLAY_OPTIONS.map(opt => ({ value: opt.id, label: opt.name }))}
                value={settings.overlay || 'none'}
                onChange={(val) => updateSetting('overlay', val)}
              />
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export { OVERLAY_OPTIONS, HERO_HEIGHTS, IMAGE_POSITIONS };
export default HeroSettings;
